import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * PhonePe Test Payment Page
 * This simulates the PhonePe payment gateway for testing purposes
 * In production, users will be redirected to actual PhonePe payment page
 */
export default function PhonePeTestPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | null>(null);

  const orderId = searchParams.get('orderId');
  const txnId = searchParams.get('txnId');
  const isTest = searchParams.get('test') === 'true';

  useEffect(() => {
    if (!isTest) {
      // If not a test, this page shouldn't be shown
      navigate('/');
      return;
    }

    // Simulate payment processing
    const timer = setTimeout(() => {
      setProcessing(false);
      // Simulate successful payment (90% success rate for testing)
      const success = Math.random() > 0.1;
      setPaymentStatus(success ? 'success' : 'failed');

      // Auto-redirect after 2 seconds
      setTimeout(() => {
        if (success && orderId) {
          // Update transaction status in localStorage
          if (txnId) {
            const txnData = localStorage.getItem(`phonepe_${txnId}`);
            if (txnData) {
              const data = JSON.parse(txnData);
              data.status = 'SUCCESS';
              localStorage.setItem(`phonepe_${txnId}`, JSON.stringify(data));
            }
          }

          // Redirect to order confirmation with payment success
          navigate(`/order-confirmation?orderId=${orderId}&payment=success&test=true`);
        } else {
          // Redirect back to checkout with error
          navigate('/checkout?payment=failed');
        }
      }, 2000);
    }, 2000);

    return () => clearTimeout(timer);
  }, [isTest, navigate, orderId, txnId]);

  if (!isTest) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        <div className="mb-6">
          <img
            src="https://www.phonepe.com/webstatic/8300/static/d146d3f76b29b6e5959a78ca1739e93f/d0c05/phonepe-logo.png"
            alt="PhonePe"
            className="h-12 mx-auto mb-4"
          />
          <div className="bg-purple-100 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full inline-block">
            TEST MODE
          </div>
        </div>

        {processing && (
          <div className="py-8">
            <Loader className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-600">Please wait while we process your payment...</p>
            <div className="mt-4 text-sm text-gray-500">
              <p>Order ID: {orderId}</p>
              <p className="text-xs mt-1">Transaction ID: {txnId}</p>
            </div>
          </div>
        )}

        {!processing && paymentStatus === 'success' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="py-8"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600">Your payment has been processed successfully.</p>
            <p className="text-sm text-gray-500 mt-2">Redirecting to order confirmation...</p>
          </motion.div>
        )}

        {!processing && paymentStatus === 'failed' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="py-8"
          >
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600">We couldn't process your payment. Please try again.</p>
            <p className="text-sm text-gray-500 mt-2">Redirecting back to checkout...</p>
          </motion.div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            🧪 This is a simulated payment page for testing purposes only.
            <br />
            In production, users will be redirected to the actual PhonePe payment gateway.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
